<template>
  <DialogHeader> Confirm your payment </DialogHeader>
  <DialogContent>
    <div
      class="w-full flex items-center justify-between pb-[15px] border-b border-gray-800/10 my-[15px]"
    >
      <div>{{ product.name }}</div>
      <div class="text-link" @click="handleChangeProduct">Change</div>
    </div>
    <div
      class="w-full flex items-center justify-between pb-[15px] border-b border-gray-800/10 mb-[15px]"
    >
      <div class="opacity-50">Total</div>
      <div class="font-medium">{{ amount }}</div>
    </div>
    <div class="w-full flex justify-between mb-5">
      <div class="opacity-50">Payment method</div>
      <div class="text-right">
        <div v-if="billing?.paymentMethod?.card" class="flex items-center">
          <CreditCard
            :type="billing?.paymentMethod?.card?.type"
            class="mr-2.5"
          />
          <div>.... .... .... {{ billing?.paymentMethod?.card?.last4 }}</div>
        </div>
        <div class="text-link" @click="handlePaymentMethodClick">
          {{ addOrChange }}
        </div>
      </div>
    </div>

    <Message v-if="error" type="error" class="mb-3">
      Could not complete payment: {{ error }}
    </Message>

    <Message class="text-[12px]!" type="default">
      By selecting "Agree and pay" you are agreeing to our
      <span>
        <a class="text-link -mr-1" href="/legal/terms" target="_blank">
          Terms
        </a>
        ,
      </span>
      <a class="text-link" href="/legal/privacy" target="_blank">
        Privacy Policy
      </a>
      and
      <span>
        <a class="text-link -mr-1" href="/legal/pricing" target="_blank">
          Pricing Terms
        </a>
        .
      </span>
    </Message>

    <UiButton
      class="w-full mt-5"
      primary
      :disabled="!billing?.paymentMethod"
      :loading="loading"
      @click="handleClick"
    >
      Agree and pay
    </UiButton>
  </DialogContent>
</template>

<script setup lang="ts">
import { Icon, UiButton, Message } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useBillingStore } from '@/features/billing/store'
import { computed, ref } from 'vue'
import CreditCard from '@/features/billing/components/CreditCard.vue'
import { formatPrice } from '@/shared/utils/money'
import { graphql } from '@/shared/graphql/router'
import { useMutation } from '@vue/apollo-composable'
import { getStripe } from '@/shared/services/stripe'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'

interface Product {
  id: string
  name: string
  description: string
  credits: number
  price: {
    amount: number
    currency: string
  }
}

const { close, closeAll, triggerEvent } = useDialog()
const { open } = useDialog()

const props = defineProps<{
  product: Product
  openCreateReport?: boolean
  onSuccess?: () => boolean | void | Promise<boolean | void>
}>()

const { mutate: purchaseReportCredits } = useMutation(
  graphql(`
    mutation PurchaseReportCredits($input: PurchaseReportCreditsInput!) {
      purchaseReportCredits(input: $input) {
        requiresAction {
          clientSecret
        }
        error {
          code
          message
        }
      }
    }
  `),
  { clientId: 'router' }
)

const billingStore = useBillingStore()
const toastStore = useToastStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const loading = ref(false)
const error = ref('')

const billing = computed(() => billingStore.billing)
const addOrChange = computed(() =>
  billing.value?.paymentMethod ? 'Change' : 'Add'
)

const amount = computed(() => {
  return formatPrice({
    amount: props.product.price.amount / 100,
    currency: props.product.price.currency,
  })
})

const handleChangeProduct = () => {
  close()
}

const handlePaymentMethodClick = () => {
  open(Dialogs.UpdatePaymentMethod, { stack: true })
  error.value = ''
}

const handleRequiresAction = async (clientSecret: string) => {
  if (webviewStore.isActive) {
    return
  }

  const stripe = await getStripe()
  if (!stripe) return

  const { error: stripeError } = await stripe.confirmCardPayment(clientSecret)

  if (stripeError) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Payment authentication failed',
      error: new Error(stripeError.message),
    })
    return false
  }

  return true
}

const handleClick = async () => {
  try {
    loading.value = true
    error.value = ''

    const response = await purchaseReportCredits({
      input: { productId: props.product.id },
    })

    const result = response?.data?.purchaseReportCredits

    if (result?.error) {
      error.value = result.error.message || 'Payment failed'
      loading.value = false
      return
    }

    if (result?.requiresAction) {
      const success = await handleRequiresAction(
        result.requiresAction.clientSecret
      )

      if (!success) {
        loading.value = false
        return
      }
    }

    userStore.updateUser({
      reportCredits:
        (userStore.user?.reportCredits || 0) + props.product.credits,
    })

    if (props.openCreateReport) {
      open(Dialogs.CreateReport)
    } else if (props.onSuccess) {
      const success = await props.onSuccess()
      if (success === false) {
        loading.value = false
        return
      }
      closeAll()
    } else {
      triggerEvent('success')
      close()
    }
  } catch (err) {
    loading.value = false
    error.value = (err as Error).message
  }
}
</script>
